import { FC, memo, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pause, Play } from '../../../../Icons';
import { useAppSelector } from '../../../../../store/store';
import { playerService } from '../../../../../services/player';
import { Episode } from '../../../../../interfaces/episode';
import useIsMobile from '../../../../../utils/isMobile';
import { TrackActionsWrapper } from '../../../../Actions/TrackActions';

interface QueueSongDetailsProps {
  song: Spotify.Track | Episode;
  isPlaying: boolean;
  queueIndex?: number;
}

const QueueSongDetails: FC<QueueSongDetailsProps> = memo(({ song, isPlaying, queueIndex }) => {
  const isMobile = useIsMobile();
  const queue = useAppSelector((state) => state.queue.queue);
  const isPaused = useAppSelector((state) => state.spotify.state?.paused);
  const [menuOpen, setMenuOpen] = useState(false);

  const onClick = async () => {
    if (!isPaused && isPlaying) {
      return playerService.pausePlayback();
    }
    if (isPlaying) {
      return playerService.startPlayback();
    } else {
      const index = queue.findIndex((q) => q.id === song.id);
      if (index !== -1) {
        return (playerService as any).skipToUpcomingQueueItem(index);
      }
    }
  };

  const image = useMemo(() => {
    if (song.type === 'track') {
      return song.album?.images?.[0]?.url ?? '';
    }

    if (song.type === 'episode') {
      const episode = song as Episode;
      return episode.images?.[0]?.url ?? episode.show?.images?.[0]?.url ?? '';
    }

    return '';
  }, [song]);

  const artistsText = useMemo(() => {
    if (song.type === 'track') {
      return song.artists
        ?.slice(0, 3)
        .map((a) => a.name)
        .join(', ') ?? '';
    }

    if (song.type === 'episode') {
      return (song as any as Episode).show?.publisher ?? (song as any as Episode).show?.name ?? '';
    }

    return '';
  }, [song]);

  return (
    <TrackActionsWrapper
      track={song as any}
      trigger={['contextMenu']}
      queueIndex={queueIndex}
    >
      <div
        className={`queue-song ${menuOpen ? 'menu-active' : ''}`}
        onClick={isMobile ? onClick : undefined}
        onDoubleClick={!isMobile ? onClick : undefined}
      >
        <div className='flex flex-row items-center' style={{ width: '100%', minWidth: 0 }}>
          <div className='queue-song-image-container' style={{ flexShrink: 0 }}>
            {!isMobile ? (
              <div className='queue-song-overlay' onClick={onClick}>
                {!isPaused && isPlaying ? <Pause /> : <Play />}
              </div>
            ) : null}

            <img alt='Album Cover' className='album-cover' src={image} />
          </div>

          <div id='song-and-artist-name' style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
            <p
              title={song.name}
              className={`text-white font-bold song-title ${isPlaying ? 'active' : ''}`}
            >
              {song.name}
            </p>

            <p className='text-gray-200 song-artist' title={artistsText}>
              {song.type === 'track' && song.artists && song.artists.length > 0 ? (
                song.artists.slice(0, 3).map((a, i) => {
                  const artistId = (a as any).id || (a.uri ? a.uri.split(':').reverse()[0] : '');
                  return (
                    <span key={(a as any).id || a.uri || i}>
                      <Link
                        to={artistId ? `/artist/${artistId}` : '#'}
                        className='queue-artist-link'
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        {a.name}
                      </Link>
                      {i < Math.min(song.artists.length, 3) - 1 ? ', ' : ''}
                    </span>
                  );
                })
              ) : (
                <span>{artistsText}</span>
              )}
            </p>
          </div>

          {/* Three Dots More Options Button */}
          <div
            className='queue-song-actions'
            onClick={(e) => {
              e.stopPropagation();
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
            }}
          >
            <TrackActionsWrapper
              track={song as any}
              trigger={['click']}
              queueIndex={queueIndex}
              onOpenChange={setMenuOpen}
            >
              <button
                className={`queue-more-btn ${menuOpen ? 'active' : ''}`}
                aria-label='More options'
                title='More options'
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <svg role='img' height='16' width='16' viewBox='0 0 16 16' fill='currentColor'>
                  <path d='M3 8a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm6.5 0a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zM16 8a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z' />
                </svg>
              </button>
            </TrackActionsWrapper>
          </div>
        </div>
      </div>
    </TrackActionsWrapper>
  );
});

export default QueueSongDetails;
